/**
 * TeacherOnboardingWizard — wizard 3 bước thiết lập hồ sơ giảng dạy khi GV đăng nhập lần đầu.
 *
 * Luồng theo cấu trúc trường học VN + hỗ trợ GV dạy thêm/trung tâm:
 *   Bước 1: Môi trường dạy (Trường học / Dạy thêm-Trung tâm / Cả hai)
 *           + Cấp học (GV trường: 1 cấp; GV dạy thêm: chọn được NHIỀU cấp)
 *           + vai trò chủ nhiệm/bộ môn (chỉ Tiểu học ở trường) + các khối đang dạy
 *   Bước 2: Môn học theo danh mục GDPT 2018 — gộp môn của tất cả cấp đã chọn
 *           (Tiểu học chủ nhiệm → tự chọn sẵn nhóm môn cơ bản; THCS có môn tích hợp)
 *   Bước 3: Tạo lớp đầu tiên (tùy chọn) + xác nhận
 *
 * Hiển thị khi: chưa hoàn thành onboarding (localStorage) VÀ chưa có lớp nào (getClasses trả rỗng).
 * Khi hoàn thành: lưu TeachingProfile lên server qua onSaveProfile (userSettings)
 * → mọi form tạo học liệu được prefill đúng môn/khối của GV.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Check, ChevronRight, X, Users, BookOpen, GraduationCap } from 'lucide-react';
import { getClasses } from '../../../services/apiService';
import { UI_CONFIG } from '../../../config';
import { GradeLevel, TeachingProfile } from '../../../types';
import {
  getGradesForLevel,
  getSubjectsForLevel,
  getHomeroomSubjects,
  SubjectInfo,
} from '../../../constants/educationTaxonomy';

const ONBOARDING_DONE_KEY = 'bookai_teacher_onboarding_done';
const ONBOARDING_CLASS_KEY = 'bookai_teacher_onboarding_class';
const ONBOARDING_SUBJECT_KEY = 'bookai_teacher_onboarding_subject';

interface Props {
  onDone: () => void;
  /** Lưu hồ sơ giảng dạy vào userSettings (App merge full object + sync backend) */
  onSaveProfile?: (profile: TeachingProfile) => void;
}

type TeachingContext = NonNullable<TeachingProfile['context']>;

const CONTEXT_OPTIONS: { key: TeachingContext; emoji: string; label: string; hint: string }[] = [
  { key: 'school', emoji: '🏫', label: 'Trường học', hint: 'Dạy chính khóa tại trường' },
  { key: 'tutoring', emoji: '🏠', label: 'Dạy thêm / Trung tâm', hint: 'Gia sư, lớp học thêm, trung tâm' },
  { key: 'both', emoji: '🔄', label: 'Cả hai', hint: 'Vừa trường vừa dạy thêm' },
];

/** Các cấp học hiển thị trong onboarding (đủ 6 cấp của GradeLevel) */
const LEVEL_OPTIONS: { level: GradeLevel; emoji: string; hint: string }[] = [
  { level: GradeLevel.MAM_NON, emoji: '🧸', hint: 'Nhà trẻ, Mẫu giáo' },
  { level: GradeLevel.TIEU_HOC, emoji: '🎒', hint: 'Lớp 1 – 5' },
  { level: GradeLevel.THCS, emoji: '📗', hint: 'Lớp 6 – 9' },
  { level: GradeLevel.THPT, emoji: '🎓', hint: 'Lớp 10 – 12' },
  { level: GradeLevel.GDTX, emoji: '🏫', hint: 'Giáo dục thường xuyên' },
  { level: GradeLevel.DAI_HOC, emoji: '🏛️', hint: 'Đại học / Cao đẳng' },
];

export const TeacherOnboardingWizard: React.FC<Props> = ({ onDone, onSaveProfile }) => {
  const [visible, setVisible] = useState(false);
  const [step, setStep] = useState(1);

  // Bước 1: môi trường dạy + cấp học (GV dạy thêm chọn được nhiều cấp) + vai trò + khối
  const [context, setContext] = useState<TeachingContext>('school');
  const [selectedLevels, setSelectedLevels] = useState<GradeLevel[]>([]);
  const [primaryRole, setPrimaryRole] = useState<'homeroom' | 'subject_teacher'>('homeroom');
  const [selectedGrades, setSelectedGrades] = useState<number[]>([]);
  // Bước 2: môn học
  const [selectedSubjects, setSelectedSubjects] = useState<string[]>([]);
  const [customSubject, setCustomSubject] = useState('');
  // Bước 3: lớp đầu tiên (tùy chọn)
  const [className, setClassName] = useState('');
  const [classSize, setClassSize] = useState('');

  const [err, setErr] = useState('');
  const classInputRef = useRef<HTMLInputElement>(null);

  // Quyết định có hiện wizard không
  useEffect(() => {
    if (localStorage.getItem(ONBOARDING_DONE_KEY)) return;
    // Kiểm tra xem đã có lớp chưa (nếu có → bỏ qua onboarding)
    getClasses()
      .then((classes) => {
        if (classes && classes.length > 0) {
          localStorage.setItem(ONBOARDING_DONE_KEY, '1');
          return;
        }
        setVisible(true);
      })
      .catch(() => {
        // Nếu lỗi fetch (backend chưa chạy), vẫn hiện wizard
        setVisible(true);
      });
  }, []);

  const multiLevel = context !== 'school';
  // GV trường Tiểu học mới có khái niệm chủ nhiệm; dạy thêm là dạng lớp bộ môn
  const isPrimaryHomeroomEligible = context === 'school' && selectedLevels.includes(GradeLevel.TIEU_HOC);

  /** Khối = hợp của các cấp đã chọn (GV dạy thêm Toán 6-12 thấy đủ 6→12) */
  const grades = useMemo(() => {
    const set = new Set<number>();
    selectedLevels.forEach(lv => getGradesForLevel(lv).forEach(g => set.add(g)));
    return [...set].sort((a, b) => a - b);
  }, [selectedLevels]);

  /** Môn = hợp của các cấp đã chọn, khử trùng theo tên (Toán chỉ hiện 1 lần) */
  const subjects = useMemo(() => {
    const seen = new Map<string, SubjectInfo>();
    selectedLevels.forEach(lv => {
      getSubjectsForLevel(lv).forEach(s => {
        if (!seen.has(s.name)) seen.set(s.name, s);
      });
    });
    return [...seen.values()];
  }, [selectedLevels]);

  const progressWidth = step === 1 ? '33%' : step === 2 ? '66%' : '100%';

  const toggleLevel = (level: GradeLevel) => {
    setSelectedLevels(prev => {
      let next: GradeLevel[];
      if (multiLevel) {
        next = prev.includes(level) ? prev.filter(l => l !== level) : [...prev, level];
      } else {
        next = prev.includes(level) ? [] : [level];
      }
      // Cấp đổi → khối/môn không còn hợp lệ, reset để tránh dữ liệu lệch cấp
      setSelectedGrades(g => g.filter(x => next.some(lv => getGradesForLevel(lv).includes(x))));
      setSelectedSubjects([]);
      return next;
    });
  };

  const toggleGrade = (g: number) => {
    setSelectedGrades(prev => (prev.includes(g) ? prev.filter(x => x !== g) : [...prev, g].sort((a, b) => a - b)));
  };

  const toggleSubject = (name: string) => {
    setSelectedSubjects(prev => (prev.includes(name) ? prev.filter(x => x !== name) : [...prev, name]));
  };

  const handleFinish = useCallback(() => {
    const subjectList = [...selectedSubjects];
    const custom = customSubject.trim();
    if (custom && !subjectList.includes(custom)) subjectList.push(custom);

    localStorage.setItem(ONBOARDING_DONE_KEY, '1');
    if (className) localStorage.setItem(ONBOARDING_CLASS_KEY, className);
    if (subjectList[0]) localStorage.setItem(ONBOARDING_SUBJECT_KEY, subjectList[0]);

    if (selectedLevels.length > 0 && onSaveProfile) {
      onSaveProfile({
        schoolLevel: selectedLevels[0],
        schoolLevels: selectedLevels,
        context,
        role: isPrimaryHomeroomEligible ? primaryRole : undefined,
        subjects: subjectList,
        grades: selectedGrades,
      });
    }

    setVisible(false);
    onDone();
  }, [selectedSubjects, customSubject, className, selectedLevels, context, isPrimaryHomeroomEligible, primaryRole, selectedGrades, onSaveProfile, onDone]);

  const goNext = useCallback(() => {
    setErr('');
    if (step === 1) {
      if (selectedLevels.length === 0) { setErr('Vui lòng chọn cấp học bạn đang dạy.'); return; }
      // Tiểu học chủ nhiệm → chọn sẵn nhóm môn cơ bản của khối (GV chỉnh lại được ở bước 2)
      if (isPrimaryHomeroomEligible && primaryRole === 'homeroom' && selectedSubjects.length === 0) {
        setSelectedSubjects(getHomeroomSubjects(selectedGrades[0]).map(s => s.name));
      }
      setStep(2);
      setTimeout(() => classInputRef.current?.focus(), 100);
    } else if (step === 2) {
      if (selectedSubjects.length === 0 && !customSubject.trim()) {
        setErr('Vui lòng chọn hoặc nhập ít nhất một môn học.');
        return;
      }
      setStep(3);
      setTimeout(() => classInputRef.current?.focus(), 100);
    }
  }, [step, selectedLevels, isPrimaryHomeroomEligible, primaryRole, selectedGrades, selectedSubjects, customSubject]);

  if (!visible) return null;

  const summarySubjects = (() => {
    const list = [...selectedSubjects];
    const custom = customSubject.trim();
    if (custom && !list.includes(custom)) list.push(custom);
    return list;
  })();

  const contextLabel = CONTEXT_OPTIONS.find(o => o.key === context)?.label || '';

  return (
    <div className="fixed inset-0 z-[100] bg-slate-900/60 backdrop-blur-md flex items-center justify-center p-4">
      <div className="bg-white rounded-3xl shadow-2xl w-full max-w-lg overflow-hidden max-h-[92vh] overflow-y-auto">

        {/* Progress bar */}
        <div className="h-1.5 bg-slate-100 sticky top-0">
          <div
            className="h-full bg-indigo-500 rounded-full transition-all duration-500"
            style={{ width: progressWidth }}
          />
        </div>

        {/* Header */}
        <div className="flex items-center justify-between px-8 pt-6 pb-2">
          <div className="flex items-center gap-2.5">
            {UI_CONFIG.APP_LOGO_URL ? (
              <img src={UI_CONFIG.APP_LOGO_URL} alt={UI_CONFIG.APP_NAME} className="w-8 h-8 rounded-xl object-contain" />
            ) : (
              <div className="w-8 h-8 bg-indigo-600 rounded-xl flex items-center justify-center text-white font-bold text-base shadow-md">
                {(UI_CONFIG.APP_NAME || 'B').charAt(0)}
              </div>
            )}
            <span className="font-bold text-slate-800 text-sm">{UI_CONFIG.APP_NAME || 'BookAI'}</span>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-xs font-bold text-slate-400 uppercase tracking-wider">Bước {step} / 3</span>
            <button
              onClick={handleFinish}
              className="p-1 rounded-lg text-slate-300 hover:text-slate-500 transition-colors"
              title="Bỏ qua"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* ===== STEP 1: Môi trường dạy + cấp học + khối ===== */}
        {step === 1 && (
          <div className="px-8 pb-8 pt-4">
            <div className="w-14 h-14 rounded-2xl bg-indigo-50 flex items-center justify-center mb-5">
              <GraduationCap className="w-7 h-7 text-indigo-600" />
            </div>
            <h2 className="text-xl font-bold text-slate-900 mb-1">Chào mừng đến {UI_CONFIG.APP_NAME || 'BookAI'}!</h2>
            <p className="text-sm text-slate-500 mb-5 leading-relaxed">
              Cho chúng tôi biết bạn dạy ở đâu và cấp nào — công cụ, môn học sẽ được tùy chỉnh theo đúng chương trình.
            </p>

            <div className="mb-4">
              <label className="block text-xs font-bold text-slate-700 mb-1.5">Bạn dạy ở đâu?</label>
              <div className="grid grid-cols-3 gap-2">
                {CONTEXT_OPTIONS.map(({ key, emoji, label, hint }) => (
                  <button
                    key={key}
                    type="button"
                    onClick={() => {
                      setContext(key);
                      // Từ dạy thêm (nhiều cấp) về trường (1 cấp) → chỉ giữ cấp đầu
                      if (key === 'school') {
                        setSelectedLevels(prev => (prev.length > 1 ? [prev[0]] : prev));
                      }
                    }}
                    className={[
                      'flex flex-col items-center gap-1 p-3 border rounded-xl transition-all text-center',
                      context === key
                        ? 'border-indigo-500 bg-indigo-50'
                        : 'border-slate-200 hover:border-indigo-400 hover:bg-indigo-50',
                    ].join(' ')}
                  >
                    <span className="text-xl">{emoji}</span>
                    <span className={`text-xs font-bold leading-tight ${context === key ? 'text-indigo-700' : 'text-slate-600'}`}>{label}</span>
                    <span className="text-[10px] text-slate-400 leading-tight">{hint}</span>
                  </button>
                ))}
              </div>
            </div>

            <div className="mb-4">
              <label className="block text-xs font-bold text-slate-700 mb-1.5">
                Cấp học{multiLevel ? ' (chọn được nhiều — vd dạy thêm Toán cả THCS lẫn THPT)' : ''}
              </label>
              <div className="grid grid-cols-3 gap-2">
                {LEVEL_OPTIONS.map(({ level, emoji, hint }) => (
                  <button
                    key={level}
                    type="button"
                    onClick={() => toggleLevel(level)}
                    className={[
                      'flex flex-col items-center gap-1 p-3 border rounded-xl transition-all text-center relative',
                      selectedLevels.includes(level)
                        ? 'border-indigo-500 bg-indigo-50'
                        : 'border-slate-200 hover:border-indigo-400 hover:bg-indigo-50',
                    ].join(' ')}
                  >
                    {multiLevel && selectedLevels.includes(level) && (
                      <span className="absolute top-1.5 right-1.5 w-4 h-4 bg-indigo-600 rounded-full flex items-center justify-center">
                        <Check className="w-2.5 h-2.5 text-white" strokeWidth={3} />
                      </span>
                    )}
                    <span className="text-xl">{emoji}</span>
                    <span className={`text-xs font-bold ${selectedLevels.includes(level) ? 'text-indigo-700' : 'text-slate-600'}`}>{level}</span>
                    <span className="text-[10px] text-slate-400 leading-tight">{hint}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Tiểu học ở trường: GV chủ nhiệm dạy hầu hết môn / GV bộ môn đặc thù */}
            {isPrimaryHomeroomEligible && (
              <div className="mb-4">
                <label className="block text-xs font-bold text-slate-700 mb-1.5">Ở trường, bạn là…</label>
                <div className="grid grid-cols-2 gap-2">
                  {([
                    { key: 'homeroom' as const, label: 'GV chủ nhiệm', hint: 'Dạy hầu hết môn của lớp mình' },
                    { key: 'subject_teacher' as const, label: 'GV bộ môn', hint: 'Tiếng Anh, Tin học, Âm nhạc…' },
                  ]).map(({ key, label, hint }) => (
                    <button
                      key={key}
                      type="button"
                      onClick={() => { setPrimaryRole(key); setSelectedSubjects([]); }}
                      className={[
                        'p-3 border rounded-xl text-left transition-all',
                        primaryRole === key
                          ? 'border-indigo-500 bg-indigo-50'
                          : 'border-slate-200 hover:border-indigo-400 hover:bg-indigo-50',
                      ].join(' ')}
                    >
                      <div className={`text-sm font-bold ${primaryRole === key ? 'text-indigo-700' : 'text-slate-700'}`}>{label}</div>
                      <div className="text-[11px] text-slate-400 leading-tight mt-0.5">{hint}</div>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {selectedLevels.length > 0 && grades.length > 0 && (
              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1.5">Các khối bạn đang dạy (chọn được nhiều)</label>
                <div className="flex gap-2 flex-wrap">
                  {grades.map((g) => (
                    <button
                      key={g}
                      type="button"
                      onClick={() => toggleGrade(g)}
                      className={[
                        'px-4 py-2 rounded-xl border text-sm font-semibold transition-all',
                        selectedGrades.includes(g)
                          ? 'border-indigo-500 bg-indigo-50 text-indigo-700'
                          : 'border-slate-200 text-slate-600 hover:border-indigo-400 hover:bg-indigo-50 hover:text-indigo-700',
                      ].join(' ')}
                    >
                      Lớp {g}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {err && <p className="text-xs text-rose-500 mt-3">{err}</p>}
            <button
              onClick={goNext}
              className="mt-6 w-full bg-indigo-600 hover:bg-indigo-700 active:scale-[0.98] text-white font-bold py-3 rounded-xl text-sm transition-all shadow-md shadow-indigo-100 flex items-center justify-center gap-2"
            >
              Tiếp theo <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        )}

        {/* ===== STEP 2: Chọn môn theo (các) cấp đã chọn ===== */}
        {step === 2 && (
          <div className="px-8 pb-8 pt-4">
            <div className="w-14 h-14 rounded-2xl bg-emerald-50 flex items-center justify-center mb-5">
              <BookOpen className="w-7 h-7 text-emerald-600" />
            </div>
            <h2 className="text-xl font-bold text-slate-900 mb-1">Môn bạn đảm nhiệm</h2>
            <p className="text-sm text-slate-500 mb-5 leading-relaxed">
              {isPrimaryHomeroomEligible && primaryRole === 'homeroom'
                ? 'Chúng tôi đã chọn sẵn nhóm môn cơ bản của GV chủ nhiệm — bạn có thể bỏ bớt hoặc thêm.'
                : `Danh mục môn ${selectedLevels.join(' + ')} theo chương trình GDPT 2018. Chọn được nhiều môn.`}
            </p>

            {subjects.length > 0 ? (
              <div className="grid grid-cols-3 gap-2 mb-4">
                {subjects.map((s) => (
                  <button
                    key={s.name}
                    type="button"
                    onClick={() => toggleSubject(s.name)}
                    className={[
                      'flex flex-col items-center gap-1.5 p-3 border rounded-xl transition-all group relative',
                      selectedSubjects.includes(s.name)
                        ? 'border-indigo-500 bg-indigo-50'
                        : 'border-slate-200 hover:border-indigo-400 hover:bg-indigo-50',
                    ].join(' ')}
                  >
                    {selectedSubjects.includes(s.name) && (
                      <span className="absolute top-1.5 right-1.5 w-4 h-4 bg-indigo-600 rounded-full flex items-center justify-center">
                        <Check className="w-2.5 h-2.5 text-white" strokeWidth={3} />
                      </span>
                    )}
                    <span className="text-xl">{s.icon}</span>
                    <span className={`text-[11px] font-bold text-center leading-tight ${selectedSubjects.includes(s.name) ? 'text-indigo-700' : 'text-slate-600 group-hover:text-indigo-700'}`}>
                      {s.name}
                    </span>
                    {s.components && (
                      <span className="text-[9px] text-slate-400 leading-tight text-center">({s.components.join(' · ')})</span>
                    )}
                  </button>
                ))}
              </div>
            ) : (
              <p className="text-xs text-slate-400 mb-3 italic">Cấp học này chưa có danh mục sẵn — hãy nhập môn bạn dạy bên dưới.</p>
            )}

            <input
              type="text"
              placeholder="Môn khác chưa có trong danh sách…"
              value={customSubject}
              onChange={(e) => setCustomSubject(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && goNext()}
              className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm text-slate-800 placeholder-slate-400 focus:outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100 transition-all"
            />
            {err && <p className="text-xs text-rose-500 mt-3">{err}</p>}

            <div className="flex gap-3 mt-6">
              <button
                onClick={() => { setStep(1); setErr(''); }}
                className="flex-1 border border-slate-200 text-slate-600 font-bold py-3 rounded-xl text-sm hover:bg-slate-50 transition-all"
              >
                Quay lại
              </button>
              <button
                onClick={goNext}
                className="flex-[2] bg-indigo-600 hover:bg-indigo-700 active:scale-[0.98] text-white font-bold py-3 rounded-xl text-sm transition-all shadow-md shadow-indigo-100 flex items-center justify-center gap-2"
              >
                Tiếp theo <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}

        {/* ===== STEP 3: Lớp đầu tiên + xác nhận ===== */}
        {step === 3 && (
          <div className="px-8 pb-8 pt-4">
            <div className="w-14 h-14 rounded-2xl bg-indigo-600 flex items-center justify-center mb-5 shadow-lg shadow-indigo-100">
              <Check className="w-7 h-7 text-white" strokeWidth={2.5} />
            </div>
            <h2 className="text-xl font-bold text-slate-900 mb-1">Gần xong rồi!</h2>
            <p className="text-sm text-slate-500 mb-5 leading-relaxed">
              Đặt tên lớp đầu tiên của bạn (tùy chọn) — mọi form soạn bài sẽ tự điền đúng môn và khối bạn dạy.
            </p>

            <div className="space-y-4 mb-6">
              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1.5">Tên lớp (tùy chọn)</label>
                <input
                  ref={classInputRef}
                  type="text"
                  maxLength={30}
                  placeholder={context === 'tutoring' ? 'Ví dụ: Toán 9 ca tối T3-T5, Nhóm Anh văn 6+7…' : 'Ví dụ: 10A1, 3A, 8B3…'}
                  value={className}
                  onChange={(e) => setClassName(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleFinish()}
                  className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm text-slate-800 placeholder-slate-400 focus:outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100 transition-all"
                />
              </div>
              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1.5">Sĩ số (tùy chọn)</label>
                <input
                  type="number"
                  min={1}
                  max={60}
                  placeholder="Ví dụ: 35"
                  value={classSize}
                  onChange={(e) => setClassSize(e.target.value)}
                  className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm text-slate-800 placeholder-slate-400 focus:outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100 transition-all"
                />
              </div>
            </div>

            {/* Summary */}
            <div className="bg-slate-50 rounded-2xl p-4 border border-slate-200 mb-6 space-y-3">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-indigo-100 flex items-center justify-center text-indigo-600 shrink-0">
                  <GraduationCap className="w-4 h-4" />
                </div>
                <div>
                  <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Môi trường · Cấp học · Khối</div>
                  <div className="text-sm font-bold text-slate-800">
                    {contextLabel} · {selectedLevels.join(', ')}
                    {isPrimaryHomeroomEligible ? (primaryRole === 'homeroom' ? ' · GV chủ nhiệm' : ' · GV bộ môn') : ''}
                    {selectedGrades.length > 0 ? ` · Lớp ${selectedGrades.join(', ')}` : ''}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-emerald-100 flex items-center justify-center text-emerald-600 shrink-0">
                  <BookOpen className="w-4 h-4" />
                </div>
                <div>
                  <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Môn đảm nhiệm</div>
                  <div className="text-sm font-bold text-slate-800">
                    {summarySubjects.length > 0 ? summarySubjects.join(', ') : '—'}
                  </div>
                </div>
              </div>
              {className && (
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-amber-100 flex items-center justify-center text-amber-600 shrink-0">
                    <Users className="w-4 h-4" />
                  </div>
                  <div>
                    <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Lớp học</div>
                    <div className="text-sm font-bold text-slate-800">
                      {className}{classSize ? ` · ${classSize} học sinh` : ''}
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => { setStep(2); setErr(''); }}
                className="flex-1 border border-slate-200 text-slate-600 font-bold py-3 rounded-xl text-sm hover:bg-slate-50 transition-all"
              >
                Quay lại
              </button>
              <button
                onClick={handleFinish}
                className="flex-[2] bg-indigo-600 hover:bg-indigo-700 active:scale-[0.98] text-white font-bold py-3.5 rounded-xl text-sm transition-all shadow-md shadow-indigo-100 flex items-center justify-center gap-2"
              >
                ✨ Bắt đầu tạo nội dung với AI
              </button>
            </div>
          </div>
        )}

      </div>
    </div>
  );
};
